import { observer } from 'mobx-react-lite'
import { useActivation } from '@/hooks/view-model-hooks'
import { useDocumentTitle } from '@/hooks/document-title'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { WorkspaceFilterViewHeadingContainer } from '@/components/WorkspaceFilterViewHeadingContainer'
import { Staffing } from '@/features/admin/staffing/components/Staffing'
import { WorkspacePostingStatusFilterContainer } from '@/features/admin/staffing/components/WorkspacePostingStatusFilter/WorkspacePostingStatusFilterContainer'
import { StaffingTableContainer } from '@/features/admin/staffing/components/StaffingTable/StaffingTableContainer'
import { StaffingDetailsPanelContainer } from '@/features/admin/staffing/components/StaffingDetailsPanel/StaffingDetailsPanelContainer'
import { CreateWorkspacePostingDialogContainer } from '@/features/admin/staffing/components/CreateWorkspacePostingDialog/CreateWorkspacePostingDialogContainer'
import { UpdateWorkspacePostingDialogContainer } from '@/features/admin/staffing/components/UpdateWorkspacePostingDialog/UpdateWorkspacePostingDialogContainer'
import { ApplicantManagementPanelContainer } from '@/features/admin/staffing/components/ApplicantManagementPanel/ApplicantManagementPanelContainer'
import { type StaffingViewModel } from '@/features/admin/staffing/StaffingViewModel'

export interface StaffingContainerProps {
  vm: StaffingViewModel
}

export const StaffingContainer = observer(function StaffingContainer({
  vm,
}: StaffingContainerProps) {
  useDocumentTitle('Staffing Manager')
  useActivation(vm)

  return vm.fetch.match({
    pending: () => <ContentLoading />,
    rejected: (err) => <ContentError error={err} />,
    resolved: () => <StaffingContentContainer vm={vm} />,
  })
})

const StaffingContentContainer = observer(function StaffingContentContainer({
  vm,
}: StaffingContainerProps) {
  return (
    <Staffing
      heading={
        <WorkspaceFilterViewHeadingContainer
          allOptionText="All Workspaces"
          primaryText="Staffing Manager"
          filter={vm.searcher.workspaceFilter}
          testId="workspace-filter-view-heading-dropdown"
        />
      }
      onNewPost={vm.createWorkspacePostingVm.modal.show}
      isSideSheetVisible={vm.staffingDetailsVm.modal.showing}
      statusFilter={
        <WorkspacePostingStatusFilterContainer
          filter={vm.searcher.statusFilter}
        />
      }
      postingCount={vm.searcher.workspacePostings.length}
      searchText={vm.searcher.searchText.value}
      onSearchTextChange={vm.searcher.searchText.change}
      table={<StaffingTableContainer vm={vm} />}
      detailsPanel={<StaffingDetailsPanelContainer vm={vm.staffingDetailsVm} />}
    >
      <CreateWorkspacePostingDialogContainer vm={vm.createWorkspacePostingVm} />
      <UpdateWorkspacePostingDialogContainer vm={vm.updateWorkspacePostingVm} />
      <ApplicantManagementPanelContainer
        vm={vm.staffingDetailsVm.applicantManagementPanelVm}
      />
    </Staffing>
  )
})
